import { useCallback, useEffect, useRef, useState } from "react";
import { ProductGalleryItem } from "../../../../../services/helpers/ProductImageHelper";

export interface UseGalleryResult {
  /** Images AND videos, in payload order — the backend's order IS the display order. */
  items: ProductGalleryItem[];
  imagesLoading: boolean;
  imageError: boolean;
  currentIndex: number;
  setCurrentIndex: (idx: number) => void;
  carouselRef: React.RefObject<HTMLDivElement>;
  activeImgRef: React.RefObject<HTMLImageElement>;
  handleCarouselScroll: React.UIEventHandler<HTMLDivElement>;
  onCarouselWheel: React.WheelEventHandler<HTMLDivElement>;
  goToImage: (idx: number) => void;
  isViewerOpen: boolean;
  openViewer: (idx: number) => void;
  closeViewer: () => void;
  viewerIndex: number;
}

/**
 * @param productId    Product whose gallery is shown.
 * @param payloadItems Gallery items (images + videos) from
 *                     productMediaResources — the ONLY media source.
 *                     Three states:
 *                     - undefined  → product not in store yet; hold the
 *                       loading state.
 *                     - []         → product loaded, no visual media:
 *                       definitive "nothing to show" (error/fallback visual).
 *                       Nothing is probed or guessed.
 *                     - [items...] → use directly. No fetches, no blobs —
 *                       the browser loads <img>/<video> sources natively.
 */
export function useGallery(
  productId: string | undefined,
  payloadItems?: ProductGalleryItem[]
): UseGalleryResult {
  const [items, setItems] = useState<ProductGalleryItem[]>([]);
  const [imagesLoading, setImagesLoading] = useState(true);
  const [imageError, setImageError] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isViewerOpen, setIsViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  const carouselRef = useRef<HTMLDivElement>(null);
  const activeImgRef = useRef<HTMLImageElement | null>(null);
  const rafId = useRef(0);
  const isAnimatingRef = useRef(false);
  const targetIndexRef = useRef<number | null>(null);
  const scrollEndTimerRef = useRef<number | null>(null);

  // Stable key so the effect re-runs only when the actual items change,
  // not on every render (callers may build the array inline).
  const payloadKey = payloadItems
    ? payloadItems.map((i) => `${i.kind}:${i.url}`).join("|")
    : undefined;

  useEffect(() => {
    if (!productId) return;

    // Product not in the store yet — keep loading, wait for the payload.
    if (payloadItems === undefined) {
      setImagesLoading(true);
      return;
    }

    // productMediaResources is the only media source: an empty list means the
    // product has no visual media (show the fallback visual). No probing, no
    // guessed storage paths.
    if (payloadItems.length === 0) {
      setItems([]);
      setImageError(true);
      setImagesLoading(false);
      return;
    }

    setItems(payloadItems);
    setImageError(false);
    setImagesLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productId, payloadKey]);

  // Scroll carousel when currentIndex changes programmatically
  useEffect(() => {
    const el = carouselRef.current;
    if (!el) return;
    const child = el.children?.[currentIndex] as HTMLElement | undefined;
    const targetLeft = child ? child.offsetLeft : currentIndex * el.clientWidth;
    try {
      el.scrollTo({ left: targetLeft, behavior: "smooth" });
    } catch {
      el.scrollLeft = targetLeft;
    }
  }, [currentIndex]);

  const handleCarouselScroll: React.UIEventHandler<HTMLDivElement> = useCallback(() => {
    const el = carouselRef.current;
    if (!el || isAnimatingRef.current) return;
    if (rafId.current) cancelAnimationFrame(rafId.current);
    rafId.current = requestAnimationFrame(() => {
      const idx = Math.round(el.scrollLeft / (el.clientWidth || 1));
      setCurrentIndex(idx);
    });
  }, []);

  const onCarouselWheel: React.WheelEventHandler<HTMLDivElement> = useCallback((e) => {
    const el = carouselRef.current;
    if (!el) return;
    if (Math.abs(e.deltaY) >= Math.abs(e.deltaX)) {
      if (e.cancelable !== false) e.preventDefault();
      el.scrollLeft += e.deltaY;
    }
  }, []);

  const goToImage = useCallback((idx: number) => {
    setCurrentIndex(idx);
    isAnimatingRef.current = true;
    targetIndexRef.current = idx;
    if (scrollEndTimerRef.current) clearTimeout(scrollEndTimerRef.current);
    scrollEndTimerRef.current = window.setTimeout(() => {
      isAnimatingRef.current = false;
      targetIndexRef.current = null;
      scrollEndTimerRef.current = null;
    }, 420);
  }, []);

  const openViewer = useCallback((idx: number) => {
    setViewerIndex(idx);
    setIsViewerOpen(true);
  }, []);

  const closeViewer = useCallback(() => setIsViewerOpen(false), []);

  return {
    items,
    imagesLoading,
    imageError,
    currentIndex,
    setCurrentIndex,
    carouselRef,
    activeImgRef,
    handleCarouselScroll,
    onCarouselWheel,
    goToImage,
    isViewerOpen,
    openViewer,
    closeViewer,
    viewerIndex,
  };
}
